import {
  MedicationSafetyExclusion,
  NotRecommendedMedication,
  PatientAnalysis,
} from "../../../../../../types/healthcare/PatientAnalysisTypes";

/**
 * Decision logic for how the comprehensive-analysis view presents medication recommendations.
 *
 * Recommendations are only generated when the practitioner opted in
 * (`include_medication_recommendations`). When they were requested but the model returned none, the
 * practitioner must be told plainly (and steered away from proceeding) rather than the tab being
 * silently hidden. When they exist, the tab is flagged for attention.
 *
 * Pure (no React / API imports) so it can be unit-tested — the renderer itself can't be imported
 * into Jest because it pulls API modules that read `import.meta.env`.
 */
export interface MedicationReviewState {
  /** The practitioner asked for new-medication recommendations. */
  requested: boolean;
  /** The medication phase has finished (output present and not still in stage_1). */
  complete: boolean;
  /** The model returned at least one new-medication recommendation. */
  hasRecommendations: boolean;
  /** Show the "Suggested Medications" tab (kept visible even when empty, once requested + complete). */
  showSuggestedTab: boolean;
  /** Recommendations were requested + complete but none returned — warn and advise not to proceed. */
  showNoRecsWarning: boolean;
  /** At least one candidate medication was dropped server-side for an absolute contraindication. */
  suppressedForSafety: boolean;
  /** The dropped medications themselves (name, red-flag key, reason) — empty when none were dropped. */
  safetyExclusions: MedicationSafetyExclusion[];
  /**
   * Catalogue medications the analysis declined on clinical judgement (no hard
   * contraindication), each with a reason. Server-derived, so complete by construction.
   *
   * ⚠️ `undefined` IS PRESERVED RATHER THAN DEFAULTED TO `[]`, unlike `safetyExclusions`
   * above, and the asymmetry is deliberate. The server REMOVES this key when no catalogue
   * was sent, specifically so "no catalogue was offered" stays distinguishable from
   * "a catalogue was offered and nothing was declined". Collapsing them here would throw
   * away the distinction the server goes out of its way to preserve. Neither case renders a
   * section today; that is a rendering decision, not a reason to lose the data.
   */
  notRecommended?: NotRecommendedMedication[];
  /**
   * The deterministic safety screener failed to run server-side — recommendations
   * were published UN-SCREENED for absolute contraindications and require explicit
   * clinical verification before prescribing.
   */
  safetyScreenFailed: boolean;
}

export function medicationReviewState(
  analysis: Pick<PatientAnalysis, "include_medication_recommendations" | "status" | "analysis_output">,
): MedicationReviewState {
  const requested = !!analysis.include_medication_recommendations;
  const medAnalysis = analysis.analysis_output?.medication_analysis;
  // status === 'stage_1_complete' means the medication phase is still running.
  const complete = !!medAnalysis && analysis.status !== "stage_1_complete";
  const hasRecommendations = (medAnalysis?.recommendations?.length ?? 0) > 0;
  const safetyExclusions = medAnalysis?.safety_exclusions ?? [];
  const notRecommended = medAnalysis?.not_recommended;
  const suppressedForSafety = safetyExclusions.length > 0;
  const safetyScreenFailed = !!medAnalysis?.safety_screen_failed;

  const showSuggestedTab = requested && complete;
  const showNoRecsWarning = requested && complete && !hasRecommendations;

  return {
    requested,
    complete,
    hasRecommendations,
    showSuggestedTab,
    showNoRecsWarning,
    suppressedForSafety,
    safetyExclusions,
    notRecommended,
    safetyScreenFailed,
  };
}
